import { ExtraFieldItem } from 'components/ExtraFieldsManager'

// SC-11487: organização do formulário público do Link de Cadastro.
// Espelha o RegisterLink::FormLayout da API: as seções vão em
// link_fields.sections e cada campo leva display_settings.

export const BASIC_SECTION = 'basic'
// Campos sem seção continuam em "Informações extras", como antes
export const EXTRAS_SECTION = ''

export interface FormSection {
  uuid: string
  title: string
  copy_customer_data: boolean
  // Seção repetível: o cliente preenche o grupo até max_items vezes e cada
  // item aparece como "<item_label> 1", "<item_label> 2"...
  repeatable?: boolean
  item_label?: string
  max_items?: number
}

export const DEFAULT_MAX_ITEMS = 3
export const MAX_ITEMS_LIMIT = 20

export interface FieldVisibleWhen {
  custom_field_uuid: string
  values: string[]
}

export interface FieldDisplaySettings {
  section?: string
  visible_when?: FieldVisibleWhen
  copy_from?: string
}

export type CopySource =
  | 'name'
  | 'document'
  | 'email'
  | 'phone'
  | 'address'
  | 'postal_code'
  | 'street'
  | 'number'
  | 'complement'
  | 'neighborhood'
  | 'city'
  | 'state'

type BasicFieldToggle = 'email' | 'phone' | 'address'

export const COPY_SOURCE_OPTIONS: Array<{
  value: CopySource
  label: string
  // Dado só existe no formulário quando o campo básico está ligado
  requires?: BasicFieldToggle
}> = [
  { value: 'name', label: 'Nome / Razão Social' },
  { value: 'document', label: 'CPF / CNPJ' },
  { value: 'email', label: 'E-mail', requires: 'email' },
  { value: 'phone', label: 'Telefone', requires: 'phone' },
  { value: 'address', label: 'Endereço completo', requires: 'address' },
  { value: 'postal_code', label: 'CEP', requires: 'address' },
  { value: 'street', label: 'Logradouro', requires: 'address' },
  { value: 'number', label: 'Número', requires: 'address' },
  { value: 'complement', label: 'Complemento', requires: 'address' },
  { value: 'neighborhood', label: 'Bairro', requires: 'address' },
  { value: 'city', label: 'Cidade', requires: 'address' },
  { value: 'state', label: 'Estado', requires: 'address' }
]

const FIELD_TYPE_FROM_INT: Record<number, string> = {
  0: 'short_text',
  1: 'radio',
  2: 'checkbox',
  3: 'accept',
  4: 'upload',
  5: 'long_text',
  6: 'date'
}

export const fieldTypeOf = (field: ExtraFieldItem): string => {
  const type = field.field_type ?? field.extra_fieldable_type
  if (typeof type === 'number') return FIELD_TYPE_FROM_INT[type] || 'short_text'
  return type || 'short_text'
}

export const isChoiceField = (field: ExtraFieldItem) =>
  ['radio', 'checkbox'].includes(fieldTypeOf(field))

export const isTextField = (field: ExtraFieldItem) =>
  ['short_text', 'long_text'].includes(fieldTypeOf(field))

export const fieldOptions = (
  field: ExtraFieldItem
): Array<{ label: string; value: string }> => {
  const options = field.options?.length
    ? field.options
    : (field.field_configuration?.options as
        | Array<{ label: string; value: string }>
        | undefined) || []

  return options.map((option: any) =>
    typeof option === 'string'
      ? { label: option, value: option }
      : { label: option.label, value: String(option.value) }
  )
}

export const sectionOf = (field: ExtraFieldItem): string =>
  field.display_settings?.section || EXTRAS_SECTION

export const newSection = (title = ''): FormSection => ({
  uuid:
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  title,
  copy_customer_data: false
})

export const normalizeSections = (raw: unknown): FormSection[] =>
  Array.isArray(raw)
    ? raw
        .filter((section) => section && section.uuid)
        .map((section) => ({
          uuid: String(section.uuid),
          title: String(section.title ?? ''),
          copy_customer_data: !!section.copy_customer_data,
          ...(section.repeatable && {
            repeatable: true,
            item_label: String(section.item_label ?? ''),
            max_items: Number(section.max_items) || DEFAULT_MAX_ITEMS
          })
        }))
    : []

// Ordem em que os grupos aparecem no formulário público
export const sectionKeys = (sections: FormSection[]) => [
  BASIC_SECTION,
  ...sections.map((section) => section.uuid),
  EXTRAS_SECTION
]

// Ordem dos campos no formulário: por seção e, dentro dela, pela lista
export const orderFieldsForForm = (
  fields: ExtraFieldItem[],
  sections: FormSection[]
) => {
  const keys = sectionKeys(sections)
  const rank = (field: ExtraFieldItem) => {
    const index = keys.indexOf(sectionOf(field))
    return index === -1 ? keys.length - 1 : index
  }

  return fields
    .map((field, index) => ({ field, index }))
    .sort((a, b) => rank(a.field) - rank(b.field) || a.index - b.index)
    .map(({ field }) => field)
}

const isRepeatableSection = (sectionKey: string, sections: FormSection[]) =>
  !!sections.find((section) => section.uuid === sectionKey)?.repeatable

// Campos de seleção que aparecem antes deste no formulário. Campo de seção
// repetível tem uma resposta por item, então só serve de condição para campos
// da mesma seção.
export const conditionCandidates = (
  field: ExtraFieldItem,
  fields: ExtraFieldItem[],
  sections: FormSection[]
) => {
  const ordered = orderFieldsForForm(fields, sections)
  const position = ordered.findIndex((item) => item.uuid === field.uuid)

  return ordered
    .slice(0, Math.max(position, 0))
    .filter(isChoiceField)
    .filter(
      (candidate) =>
        !isRepeatableSection(sectionOf(candidate), sections) ||
        sectionOf(candidate) === sectionOf(field)
    )
}

export const copySourceOptions = (basicFields: Record<string, boolean>) =>
  COPY_SOURCE_OPTIONS.filter(
    (option) => !option.requires || basicFields[option.requires]
  )

// Gatilho que ficou depois do campo (arrastado ou trocado de seção): não é
// apagado sozinho, a tela avisa e a API recusa até o lojista corrigir.
export const hasMisplacedCondition = (
  field: ExtraFieldItem,
  fields: ExtraFieldItem[],
  sections: FormSection[]
) => {
  const condition = field.display_settings?.visible_when
  if (!condition) return false

  return !conditionCandidates(field, fields, sections).some(
    (candidate) => candidate.uuid === condition.custom_field_uuid
  )
}

// Motivo de a condição do campo não valer, na mesma regra da API (null = ok)
export const conditionProblem = (
  field: ExtraFieldItem,
  fields: ExtraFieldItem[],
  sections: FormSection[]
): string | null => {
  const condition = field.display_settings?.visible_when
  if (!condition) return null

  const trigger = fields.find(
    (item) => item.uuid === condition.custom_field_uuid
  )
  const triggerName = trigger?.name || 'O campo da condição'

  if (
    trigger &&
    isRepeatableSection(sectionOf(trigger), sections) &&
    sectionOf(trigger) !== sectionOf(field)
  ) {
    return `"${field.name}" só pode depender de "${triggerName}" se estiver na mesma seção, porque ela repete itens.`
  }
  if (hasMisplacedCondition(field, fields, sections)) {
    return `"${triggerName}" precisa aparecer antes de "${field.name}" no formulário.`
  }
  if (condition.values.length === 0) {
    return `Escolha ao menos uma resposta para exibir o campo "${field.name}".`
  }
  return null
}

// Tira referências que deixaram de existir (seção apagada, botão de cópia
// desligado, gatilho removido ou que virou outro tipo, opção que sumiu). Sem
// isso a API recusa o link inteiro na hora de salvar.
export const sanitizeDisplaySettings = (
  fields: ExtraFieldItem[],
  sections: FormSection[]
): ExtraFieldItem[] => {
  const sectionsByUuid = new Map(sections.map((s) => [s.uuid, s]))

  let current = fields.map((field) => {
    const settings = { ...(field.display_settings || {}) }
    const section = settings.section

    if (section && section !== BASIC_SECTION && !sectionsByUuid.has(section)) {
      delete settings.section
    }

    if (
      settings.copy_from &&
      (!isTextField(field) ||
        !sectionsByUuid.get(settings.section || '')?.copy_customer_data)
    ) {
      delete settings.copy_from
    }

    return { ...field, display_settings: settings }
  })

  current = current.map((field) => {
    const condition = field.display_settings?.visible_when
    if (!condition) return field

    const trigger = current.find(
      (candidate) =>
        candidate.uuid === condition.custom_field_uuid &&
        candidate.uuid !== field.uuid &&
        isChoiceField(candidate)
    )
    const optionValues = trigger
      ? fieldOptions(trigger).map((o) => o.value)
      : []
    const values = condition.values.filter((value) =>
      optionValues.includes(value)
    )

    if (trigger && values.length > 0) {
      return {
        ...field,
        display_settings: {
          ...field.display_settings,
          visible_when: { ...condition, values }
        }
      }
    }

    const settings = { ...field.display_settings }
    delete settings.visible_when
    return { ...field, display_settings: settings }
  })

  return current
}

export const displaySettingsPayload = (
  field: ExtraFieldItem
): FieldDisplaySettings | null => {
  const settings = field.display_settings || {}
  const payload: FieldDisplaySettings = {}

  if (settings.section) payload.section = settings.section
  if (settings.visible_when?.custom_field_uuid) {
    payload.visible_when = settings.visible_when
  }
  if (settings.copy_from) payload.copy_from = settings.copy_from

  return Object.keys(payload).length > 0 ? payload : null
}

// Checagem antes de salvar, com as mesmas regras da API
export const layoutProblems = (
  fields: ExtraFieldItem[],
  sections: FormSection[]
): string[] => {
  const problems: string[] = []

  if (sections.some((section) => !section.title.trim())) {
    problems.push('Dê um nome para todas as seções do formulário.')
  }

  sections
    .filter((section) => section.repeatable)
    .forEach((section) => {
      const max = Number(section.max_items)
      if (!Number.isInteger(max) || max < 2 || max > MAX_ITEMS_LIMIT) {
        problems.push(
          `A seção "${
            section.title.trim() || 'sem nome'
          }" precisa permitir entre 2 e ${MAX_ITEMS_LIMIT} itens.`
        )
      }
    })

  orderFieldsForForm(fields, sections).forEach((field) => {
    const problem = conditionProblem(field, fields, sections)
    if (problem) problems.push(problem)
  })

  return problems
}
